// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#fleet-reporting-enforcement
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CloudOff } from 'lucide-react';

import type { CurrentUser } from '@/lib/auth';
import type { FleetReportingEnforcement } from '@greenlight/shared';

interface FleetReportingBannerProps {
  enforcement: FleetReportingEnforcement;
  role: CurrentUser['role'];
}

// Written out rather than composed, so Tailwind's scanner sees whole classes.
const TONE = {
  warning: {
    container: 'border-[hsl(var(--status-amber))] bg-[hsl(var(--status-amber)/0.15)]',
    icon: 'text-[hsl(var(--status-amber-text))]',
  },
  degraded: {
    container: 'border-[hsl(var(--status-red))] bg-[hsl(var(--status-red)/0.15)]',
    icon: 'text-[hsl(var(--status-red-text))]',
  },
} as const;

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Persistent banner shown on every dashboard route while this install cannot
 * deliver fleet health reports to Shift (docs/11 §Fleet reporting enforcement).
 * Amber counts down to the date building stops; red says it already has. Every
 * role sees it — a builder whose agent is being rejected needs the reason — but
 * only org_admin gets the `/internal-health` link, since that page is theirs.
 *
 * Non-dismissable by design: the only thing that clears it is a successful
 * report delivery, which clears it automatically.
 */
export function FleetReportingBanner({
  enforcement,
  role,
}: FleetReportingBannerProps): React.JSX.Element | null {
  if (enforcement.state === 'ok') return null;

  const degraded = enforcement.state === 'degraded';
  const tone = degraded ? TONE.degraded : TONE.warning;

  return (
    <div
      role="region"
      aria-label="Fleet reporting status"
      data-testid="fleet-reporting-banner"
      data-state={enforcement.state}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:px-6 ${tone.container}`}
    >
      <CloudOff className={`size-4 shrink-0 ${tone.icon}`} aria-hidden />
      <p className="min-w-0 flex-1 text-[13px] text-foreground">
        <span className="font-semibold">
          {degraded
            ? 'Building is disabled — Greenlight cannot reach Shift fleet reporting.'
            : 'Greenlight cannot reach Shift fleet reporting.'}
        </span>{' '}
        <span className="text-muted-foreground">
          {degraded
            ? 'Creating and deploying apps is blocked until one report is delivered. Running apps keep serving.'
            : enforcement.degrade_at !== null
              ? `Building will be disabled on ${formatDay(enforcement.degrade_at)}.`
              : 'Building will be disabled if this continues.'}
        </span>
      </p>
      {role === 'org_admin' && (
        <Link
          to="/internal-health"
          className="inline-flex h-11 shrink-0 items-center text-[13px] font-medium text-link underline-offset-4 hover:underline md:h-9"
        >
          View delivery diagnostics
        </Link>
      )}
    </div>
  );
}
